"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;

class ColorConverters {
  static rgbToHsv(rgb) {
    var h = 0;
    var r = rgb.r;
    var g = rgb.g;
    var b = rgb.b;
    var min = r < g && r < b ? r : g < b ? g : b; // faster version of Math.min(r, g, b);

    var v = r > g && r > b ? r : g > b ? g : b; // faster version of Math.max(r, g, b)

    var s = v === 0 ? 0 : (v - min) / v;
    var delta = s === 0 ? 0.00001 : v - min;

    switch (v) {
      case r:
        h = (g - b) / delta;
        break;

      case g:
        h = 2 + (b - r) / delta;
        break;

      case b:
        h = 4 + (r - g) / delta;
        break;
    }

    return {
      h: (1000 + h / 6) % 1,
      s,
      v
    };
  }

  static hsvToRgb(hsv) {
    var h = hsv.h;
    var s = hsv.s;
    var v = hsv.v;
    h = (h + 1000) % 1; // keep in positive 0-1 range.

    var r = 0;
    var g = 0;
    var b = 0;
    var i = h * 6 >> 0;
    var f = h * 6 - i;
    var p = v * (1 - s);
    var q = v * (1 - s * f);
    var t = v * (1 - s * (1 - f));

    switch (i) {
      case 0:
        r = v;
        g = t;
        b = p;
        break;

      case 1:
        r = q;
        g = v;
        b = p;
        break;

      case 2:
        r = p;
        g = v;
        b = t;
        break;

      case 3:
        r = p;
        g = q;
        b = v;
        break;

      case 4:
        r = t;
        g = p;
        b = v;
        break;

      case 5:
        r = v;
        g = p;
        b = q;
        break;
    }

    return {
      r,
      g,
      b
    };
  }

  static rgbToCmyk(rgb) {
    var c = 1 - rgb.r;
    var m = 1 - rgb.g;
    var y = 1 - rgb.b;
    var k = c < m && c < y ? c : m < y ? m : y; // faster version of Math.min(c, m, y);

    if (k === 1) {
      c = m = y = 0;
    } else {
      c = (c - k) / (1 - k);
      m = (m - k) / (1 - k);
      y = (y - k) / (1 - k);
    }

    return {
      c,
      m,
      y,
      k
    };
  }

  static cmykToRgb(cmyk) {
    var k = cmyk.k;
    return {
      r: 1 - (cmyk.c * (1 - k) + k),
      g: 1 - (cmyk.m * (1 - k) + k),
      b: 1 - (cmyk.y * (1 - k) + k)
    };
  }

  static rgbToXyz(rgb) {
    var r = rgb.r;
    var g = rgb.g;
    var b = rgb.b;
    r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
    g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
    b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92; // GDS: The last multiplier on x & z are to normalize to white, 2 degree D65.
    // GDS: I'm not entirely certain if this should live here or in the lab conversion, or if it is necessary when we use fp10's color correction.

    var x = (0.4124 * r + 0.3576 * g + 0.1805 * b) * 100 / 95.047;
    var y = 0.2126 * r + 0.7152 * g + 0.0722 * b; // * 100/100

    var z = (0.0193 * r + 0.1192 * g + 0.9505 * b) * 100 / 108.883;
    return {
      x,
      y,
      z
    };
  }

  static xyzToRgb(xyz) {
    var x = xyz.x;
    var y = xyz.y;
    var z = xyz.z; // multiply by white point, 2 degree D65
    // GDS: I'm not entirely certain if this should live here or in the lab conversion, or if it is necessary when we use fp10's color correction.

    x *= 95.047 / 100; //y *= 100/100;

    z *= 108.883 / 100;
    var r = 3.24063 * x - 1.53721 * y - 0.498629 * z;
    var g = -0.968931 * x + 1.87576 * y + 0.0415175 * z;
    var b = 0.0557101 * x - 0.204021 * y + 1.057 * z;
    r = r > 0.0031308 ? 1.055 * Math.pow(r, 0.4167) - 0.055 : 12.92 * r;
    g = g > 0.0031308 ? 1.055 * Math.pow(g, 0.4167) - 0.055 : 12.92 * g;
    b = b > 0.0031308 ? 1.055 * Math.pow(b, 0.4167) - 0.055 : 12.92 * b;
    return {
      r,
      g,
      b
    };
  }

  static rgbToLab(rgb) {
    var xyz = this.rgbToXyz(rgb);
    var x = xyz.x;
    var y = xyz.y;
    var z = xyz.z;
    var fx = x > 0.008856 ? Math.pow(x, 1 / 3) : 7.787 * x + 0.1379;
    var fy = y > 0.008856 ? Math.pow(y, 1 / 3) : 7.787 * y + 0.1379;
    var fz = z > 0.008856 ? Math.pow(z, 1 / 3) : 7.787 * z + 0.1379;
    return {
      l: (116 * fy - 16) / 100.00,
      a: (500 * (fx - fy) + 0x80) / 0xFF,
      b: (200 * (fy - fz) + 0x80) / 0xFF
    };
  }

  static labToRgb(lab) {
    var y = (lab.l * 100 + 16) / 116;
    var x = y + (lab.a * 0xFF - 0x80) / 500;
    var z = y - (lab.b * 0xFF - 0x80) / 200;
    return this.xyzToRgb({
      x: x > 0.2069 ? x * x * x : 0.1284 * (x - 0.1379),
      y: y > 0.2069 ? y * y * y : 0.1284 * (y - 0.1379),
      z: z > 0.2069 ? z * z * z : 0.1284 * (z - 0.1379)
    });
  } // lab = [l, a, b]


  static labToLAB(lab) {
    var op = [];
    op[0] = lab[0] * 100;
    op[1] = lab[1] * 0xFF - 0x80;
    op[2] = lab[2] * 0xFF - 0x80;
    return op;
  } // lab = [L, A, B]


  static labToLABInverse(lab) {
    var op = [];
    op[0] = lab[0] / 100;
    op[1] = (lab[1] + 0x80) / 0xFF;
    op[2] = (lab[2] + 0x80) / 0xFF;
    return op;
  }

  static valuesToRgb(mode, values) {
    var rgb, lab, cmyk, hsv;

    switch (mode) {
      case 'lab':
        lab = {
          l: values[0],
          a: values[1],
          b: values[2]
        };
        rgb = this.labToRgb(lab);
        break;

      case 'rgb':
        rgb = {
          r: values[0],
          g: values[1],
          b: values[2]
        };
        break;

      case 'cmyk':
        cmyk = {
          c: values[0],
          m: values[1],
          y: values[2],
          k: values[3]
        };
        rgb = this.cmykToRgb(cmyk);
        break;

      case 'hsv':
        hsv = {
          h: values[0],
          s: values[1],
          v: values[2]
        };
        rgb = this.hsvToRgb(hsv);
        break;
    }

    function limit01(val) {
      return val < 0 ? 0 : val > 1 ? 1 : val;
    }

    rgb = {
      r: limit01(rgb.r),
      g: limit01(rgb.g),
      b: limit01(rgb.b)
    };
    return rgb;
  }

  static valuesToHex(mode, values) {
    var rgb = this.valuesToRgb(mode, values);
    return this._hexToString((Math.round(rgb.r * 255) << 16 | Math.round(rgb.g * 255) << 8 | Math.round(rgb.b * 255)) >>> 0).toUpperCase();
  }

  static _hexToString(value) {
    var hex = value.toString(16);
    hex = '000000'.substr(0, 6 - hex.length) + hex;
    return hex;
  }

  static RGBToHex(rgb) {
    let r = rgb.r.toString(16),
        g = rgb.g.toString(16),
        b = rgb.b.toString(16);
    if (r.length === 1) r = '0' + r;
    if (g.length === 1) g = '0' + g;
    if (b.length === 1) b = '0' + b;
    return '#' + r + g + b;
  }

}

exports.default = ColorConverters;